import {
    BorderOptions,
    CalendarState,
    DateMetaData,
    ExtraPageInfo,
    ModuleInformation,
} from "burde";
import { getMetadata } from "metadata/service";
import moment from "moment";
import {
    A5CalendarNumberOfExtraPages,
    B5CalendarNumberOfExtraPages,
    CalendarSizes,
    CalendarType,
    ExtraPagePageSize,
} from "svg/constants";
import {
    DefaultCoverTextCalendarA5,
    DefaultCoverTextCalendarA6,
    DefaultCoverTextCalendarB5,
    DefaultCoverTextNotebookA5,
    DefaultCoverTextNotebookA6,
    DefaultCoverTextNotebookB5,
    DefaultSize,
    DefaultType,
    NavigationComponents,
} from "ui/constants";
import { SvgCalendarLayout } from "ui/constants";

export const getCalendarMaxNumberOfExtraPages = (
    type: CalendarType,
    size: string,
) => {
    if (size.toLowerCase() === CalendarSizes.B5.toLowerCase())
        return B5CalendarNumberOfExtraPages;

    return A5CalendarNumberOfExtraPages;
};

export const getDefaultComponentRoute = () => {
    const defaultComponent = NavigationComponents[0];
    const route = defaultComponent.route;
    let formattedRoute = route.replace(
        "{calendarType}",
        DefaultType.toLowerCase(),
    );
    formattedRoute = route.replace("{calendarSize}", DefaultSize.toLowerCase());
    return formattedRoute;
};

export const getComponentRoute = (
    route: string,
    calendarSize: string,
    calendarType: string,
) => {
    let formattedRoute = route.replace(
        "{calendarType}",
        calendarType.toLowerCase(),
    );
    formattedRoute = formattedRoute.replace(
        "{calendarSize}",
        calendarSize.toLowerCase(),
    );
    return formattedRoute;
};

// Should run whenever the calendar layout changes
export const calendarDayModuleCheck = (
    dayModules: ModuleInformation[],
    svgCalendarLayout: SvgCalendarLayout,
    removeDayModule: (slotId: number) => any,
) => {
    if (dayModules.length > svgCalendarLayout.maxDayModules) {
        for (
            let i = svgCalendarLayout.maxDayModules;
            i < dayModules.length;
            i++
        ) {
            removeDayModule(dayModules[i].slotId);
        }
    }
};

// Should run whenever the calendar layout changes
export const calendarWeekModuleCheck = (
    weekModules: ModuleInformation[],
    svgCalendarLayout: SvgCalendarLayout,
    removeWeekModule: (slotId: number) => any,
) => {
    if (weekModules.length > svgCalendarLayout.maxWeekModules) {
        for (
            let i = svgCalendarLayout.maxWeekModules;
            i < weekModules.length;
            i++
        ) {
            removeWeekModule(weekModules[i].slotId);
        }
    }
};

// Should run whenever the custom number of extra pages changes if the calendar is a notebook
// or when the calendarType changes.
export const calendarExtraPageCheck = (
    extraPages: number[],
    calendarType: CalendarType,
    calendarSize: string,
    customNumberOfEndPages: number,
    updateExtraPageCollection: (value: number[]) => any,
) => {
    const calendarNumberOfExtraPages = getCalendarMaxNumberOfExtraPages(
        calendarType,
        calendarSize,
    );

    if (calendarType.toLowerCase() === CalendarType.Notebook.toLowerCase()) {
        if (extraPages.length * ExtraPagePageSize > customNumberOfEndPages) {
            const filteredExtraPages = extraPages.slice(
                0,
                customNumberOfEndPages / 2,
            );
            updateExtraPageCollection(filteredExtraPages);
        }
    } else if (
        extraPages.length * ExtraPagePageSize >
        calendarNumberOfExtraPages
    ) {
        const filteredExtraPages = extraPages.slice(
            0,
            calendarNumberOfExtraPages / 2,
        );
        updateExtraPageCollection(filteredExtraPages);
    }
};

export const getDefaultCoverTextByCalendarSizeAndCalendarType = (
    calendarSize: string,
    calendarType: CalendarType,
) => {
    if (calendarType.toLowerCase() === CalendarType.Calendar.toLowerCase()) {
        if (calendarSize === CalendarSizes.A6) {
            return DefaultCoverTextCalendarA6;
        } else if (calendarSize === CalendarSizes.B5) {
            return DefaultCoverTextCalendarB5;
        } else {
            return DefaultCoverTextCalendarA5;
        }
    } else if (
        calendarType.toLowerCase() === CalendarType.Notebook.toLowerCase()
    ) {
        if (calendarSize === CalendarSizes.A6) {
            return DefaultCoverTextNotebookA6;
        }

        if (calendarSize === CalendarSizes.B5) {
            return DefaultCoverTextNotebookB5;
        }
    }

    return DefaultCoverTextNotebookA5;
};

export const getSelectedBorderImageData = (
    borderOptions: BorderOptions | null,
    borderId: number | null,
) => {
    if (borderOptions === null || borderId === null) {
        return null;
    }

    let imageData = null;

    for (let i = 0; i < borderOptions.imageResources.length; i++) {
        const result = borderOptions.imageResources[i].images.find(
            (image) => image.id === borderId,
        );

        if (result !== undefined) {
            imageData = result;
            break;
        }
    }

    return imageData;
};

export const fetchMetadata = async (startDate: string, culture: string) => {
    const momentStartDate = moment(startDate, "YYYY-MM-DD")
        .clone()
        .startOf("month");

    const metadataResult = await getMetadata(
        culture,
        momentStartDate
            .clone()
            .startOf("isoWeek")
            .subtract(1, "weeks")
            .format("YYYY-MM-DD"),
        7,
    );

    return metadataResult || new Array<DateMetaData>();
};

export const getExtraPageInfoFromExtraPages = (extraPages: number[]) => {
    const extraPageInfo: ExtraPageInfo[] = [];

    for (let i = 0; i < extraPages.length; i++) {
        extraPageInfo.push({
            extraPageId: extraPages[i],
            order: i + 1,
        });
    }

    return extraPageInfo;
};
